import { parseClientApprovalAction } from '@common/enums';
import { Box, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ConfirmationPageWrapper from '@/components/ConfirmationPageWrapper';
import {
    checkStatusOfApprovalEmail,
    getClientApprovalStatus,
} from '@/services/EmailApprovalService';
import useClientApprovalPageStore from '@/stores/useClientApprovalPageStore';
import ClientApprovalConfirmation from './ClientResponseContent/ClientApprovalConfirmation';
import { ClientResponseContent } from './ClientResponseContent/ClientResponseContent';

export default function ClientApprovalPage() {
    const theme = useTheme();
    const { primary } = theme.palette;
    const { instanceId, prospectId } = useParams();
    const [
        isLoading,
        approvalStatusData,
        statusOfApprovalEmail,
        onChangeValues,
        fetchIntakeFormStatus,
    ] = useClientApprovalPageStore((state) => [
        state.isLoading,
        state.approvalStatusData,
        state.statusOfApprovalEmail,
        state.onChangeValues,
        state.fetchIntakeFormStatus,
    ]);
    const [searchParams] = useSearchParams();
    const action = searchParams.get('Action');
    const logoFileId = searchParams.get('logoUrl');
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const commonStyles = {
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
    };

    const handleClientApprovalStatus = async () => {
        if (!instanceId || !prospectId) return;
        onChangeValues('acceptLoading', true);
        try {
            const approvalResp = await getClientApprovalStatus(
                instanceId,
                prospectId,
                parseClientApprovalAction(action),
                null,
            );
            onChangeValues('approvalStatusData', approvalResp.data);
            onChangeValues('approvalConfirmationStepper', 1);
            onChangeValues('acceptLoading', false);
        } catch (error: any) {
            console.error(error);
            if (error?.response?.data?.Status === 'ClientAlreadyAccepted') {
                await fetchIntakeFormStatus(instanceId, prospectId);
            }
            onChangeValues('acceptLoading', false);
            onChangeValues('approvalStatusData', {
                Status: error?.response?.data?.Status || 'Failed',
                Message: error?.response?.data?.Message,
            });
        }
    };

    const handleCheckApprovalEmailStatus = async () => {
        if (!instanceId || !prospectId) return;
        onChangeValues('isLoading', true);
        try {
            const statusOfEmail = await checkStatusOfApprovalEmail(instanceId, prospectId);
            onChangeValues('statusOfApprovalEmail', statusOfEmail.data);
            onChangeValues('isLoading', false);
        } catch (error: unknown) {
            console.error(error);
            onChangeValues('isLoading', false);
        }
    };

    useEffect(() => {
        handleCheckApprovalEmailStatus();
    }, [instanceId, prospectId]);

    const checkForClientApprovalStatus = async () => {
        onChangeValues('isLoading', true);
        await handleClientApprovalStatus();
        onChangeValues('isLoading', false);
    };

    useEffect(() => {
        if (action !== 'RequestCall' && action !== 'Declined') {
            if (
                statusOfApprovalEmail &&
                statusOfApprovalEmail.Status !== 'Pending' &&
                statusOfApprovalEmail.Status !== 'RequestACall'
            ) {
                checkForClientApprovalStatus();
            }
        } else {
            // set the status to success so at least the ClientResponseContent component can render the correct state
            onChangeValues('approvalStatusData', {
                Status: 'Success',
                Message: '',
            });
        }
    }, [instanceId, prospectId, action, statusOfApprovalEmail]);

    const shouldRenderClientConfirmation =
        action !== 'Declined' &&
        action !== 'RequestCall' &&
        statusOfApprovalEmail?.Status &&
        (statusOfApprovalEmail.Status === 'Pending' ||
            statusOfApprovalEmail.Status === 'RequestACall') &&
        approvalStatusData.Status !== 'NewManualQuoteSent';

    return (
        <ConfirmationPageWrapper>
            <Box
                sx={{
                    ...commonStyles,
                    maxWidth: '1216px',
                    width: '95%',
                    gap: '16px',
                    margin: '32px 0px',
                }}
            >
                <Box
                    sx={{
                        ...commonStyles,
                        gap: '16px',
                        margin: '0px',
                    }}
                >
                    <Box
                        sx={
                            isMobile
                                ? {
                                      ...commonStyles,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      backgroundColor: 'white',
                                      borderRadius: '8px',
                                      padding: '16px',
                                      gap: '16px',
                                      alignItems: 'flex-start',
                                      borderTop: `12px solid ${primary.P_450}`,
                                  }
                                : {
                                      ...commonStyles,
                                      display: 'flex',
                                      flexDirection: 'row',
                                      backgroundColor: 'white',
                                      borderRadius: '8px',
                                      padding: '32px',
                                      gap: logoFileId ? '32px' : '0px',
                                      alignItems: 'flex-start',
                                      borderTop: `16px solid ${primary.P_450}`,
                                  }
                        }
                    >
                        {isLoading ? (
                            <Box>
                                <Skeleton variant="rounded" width={200} height={200} />
                            </Box>
                        ) : (
                            logoFileId &&
                            !isMobile && (
                                <Box>
                                    {/* Render Image only when image file ID is available */}
                                    <img
                                        alt="Law firm logo"
                                        style={{
                                            borderRadius: '4px',
                                            height: isMobile ? '80px' : '200px',
                                            width: isMobile ? '80px' : '200px',
                                        }}
                                        src={`${
                                            import.meta.env.VITE_APP_API_URL
                                        }/api/v1/files/${logoFileId}`}
                                    />
                                </Box>
                            )
                        )}
                        {isLoading ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: '8px',
                                }}
                            >
                                <Skeleton variant="text" sx={{ fontSize: '24px', width: '50%' }} />
                                <Skeleton variant="text" sx={{ fontSize: '14px', width: '40%' }} />
                            </Box>
                        ) : (
                            <Box sx={{ width: '100%', height: '100%' }}>
                                {shouldRenderClientConfirmation ? (
                                    <ClientApprovalConfirmation
                                        onAcceptClick={() => {
                                            handleClientApprovalStatus();
                                        }}
                                    />
                                ) : (
                                    <ClientResponseContent
                                        emailResponseAction={action ? action : ''}
                                        status={approvalStatusData.Status || 'Failed'}
                                        failedMessage={approvalStatusData.Message}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </ConfirmationPageWrapper>
    );
}
